<script setup lang="ts">
    import Form from '@cwd/shared/Components/Form/Form.vue';
    import { Data, FormProps } from '@cwd/shared/components/Form/Form';
    import { bsAlert } from '@shared/components/BsAlerts';
    import Loading from '@shared/components/Loading.vue';
    import { onMounted, reactive } from 'vue';
    import { getUsersInGroups, getUsersToAddToGroups, Group, GroupUserReference, removeUserFromGroup, saveGroup } from './Groups.access';
    import { addUsersToGroup, deleteGroupById, saveGroupInfo } from './services/groupService';
    import Tabs, { tab } from '@shared/components/Tabs.vue';
    import List from '@shared/components/List.vue';
    import MultiSelect from '@shared/components/MultiSelect/MultiSelect.vue';
    import { $Resources } from '@cwd/lib/main';

    const props = defineProps<{
        group: Group;
    }>();

    const emit = defineEmits(['back']);

    const state = reactive<{
        group: Group | null;
        isLoading: boolean;
        groupForm: FormProps;
        showAddUser: boolean;
        users: GroupUserReference[];
        newUserIds: string[];
    }>({
        group: null,
        isLoading: false,
        groupForm: {
            data: {},
        },
        showAddUser: false,
        users: [],
        newUserIds: [],
    });

    onMounted(async () => {
        state.isLoading = true;
        const members = await getUsersInGroups(props.group.GroupId);
        state.group = props.group;
        state.group.Users = members;
        setupGroupFormData();
        state.isLoading = false;
    });

    const selectedUsersMapped = () => {
        return state.newUserIds.map((id) => {
            return state.users.find((user) => user.UserId === id);
        });
    };

    const handleAddUsers = async () => {
        if (!state.group) return;
        await addUsersToGroup(state.group!.GroupId, state.newUserIds);
        const users = await getUsersInGroups(state.group!.GroupId);
        state.group = { ...state.group, Users: users };
        state.newUserIds = [];
        state.showAddUser = false;
    };

    const handleRemoveUser = async (user: GroupUserReference) => {
        try {
            await removeUserFromGroup(state.group!.GroupId, user.UserId);
            bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.SuccessRemoveUser, type: 'success' });
            state.group!.Users = state.group?.Users?.filter((u) => u.UserId !== user.UserId) || [];
        } catch (e) {
            bsAlert({ title: 'Error', content: $Resources.ErrorSendValidationBody, type: 'danger' });
        }
    };

    const getUsers = async () => {
        state.isLoading = true;
        const users = await getUsersToAddToGroups();
        state.users = users.filter((user) => !state.group?.Users?.some((u) => u.UserId === user.UserId));
        state.isLoading = false;
    };

    async function saveGroupDetails(_: Event, formData: Data) {
        if (state.isLoading === true || !state.group) return;
        state.isLoading = true;
        state.group.Name = formData['Name'];
        await saveGroupInfo({ name: state.group.Name, groupId: state.group.GroupId });
        state.isLoading = false;
    }

    async function deleteGroup() {
        await deleteGroupById(state.group?.GroupId || props.group.GroupId);
        back();
    }

    function back() {
        emit('back', null);
    }

    function setupGroupFormData() {
        state.groupForm.data = {
            Name: {
                modelValue: state.group?.Name,
                required: true,
                input: {
                    label: $Resources.AclGroupTableName,
                },
            },
        };
    }
</script>

<template>
    <div>
        <p class="fw-600 m-lg-t m-xs-b">
            <a href="#" @click="back">
                <span class="glyphicon glyphicon-chevron-left c-light" aria-hidden="true"></span>
                {{ $Resources.AclGroupHeading }}
            </a>
        </p>
    </div>

    <Loading :show="state.isLoading" loadingStyle="progress-bar"></Loading>

    <template v-if="state.group != null">
        <div class="header-wrapper">
            <h3>
                {{ state.group.Name }}
            </h3>
            <button
                class="btn btn-danger"
                v-popconfirm="{
                    body: $Resources.AclDeleteGroupText,
                    placement: 'left',
                    callback: deleteGroup,
                }">
                {{ $Resources.AclGroupUserRemove }}
            </button>
        </div>

        <p>
            <strong>{{ $Resources.AclGroupTableUsers }}:</strong>
            {{ props.group.UserCount }} |
            <strong>{{ $Resources.AclGroupTableObjects }}:</strong>
            {{ props.group.ObjectCount }}
        </p>
        <tabs navClass="tabs list-unstyled">
            <tab :title="$Resources.AclGroupTitle">
                <Form
                    class="fieldset-grid"
                    :data="state.groupForm.data"
                    @update:isValid="(key, newVal) => (state.groupForm!.data[key].isValid = newVal)"
                    @update:modelValue="(key, newVal) => (state.groupForm.data[key].modelValue = newVal)"
                    @onSubmit="saveGroupDetails"></Form>
            </tab>
            <tab :title="$Resources.AccountUsersHeading">
                <List
                    itemId="UserId"
                    :items="state.group.Users"
                    :loading="state.isLoading"
                    displayAs="table"
                    :table-headers="[
                        $Resources.AccountDefaultReplyToAddressLabel,
                        $Resources.AccountSubscriptionsName,
                        $Resources.AccountSettingsLogotypeRemoveLabel,
                    ]"
                    :perPage="10"
                    :filterKeys="['Username', 'Name']">
                    <template #toolbar>
                        <div v-if="!state.showAddUser">
                            <button class="btn btn-primary" @click="state.showAddUser = true" type="button">
                                {{ $Resources.AclGroupAddUser }}
                            </button>
                        </div>

                        <div v-else>
                            <div class="d-inline-flex gap-sm flex-grow-1 align-items-end">
                                <MultiSelect
                                    class="autocomplete-label-sr-only"
                                    :items="state.users"
                                    :selectedItems="selectedUsersMapped()"
                                    :label="$Resources.AclEditorSearchLabel"
                                    selectProp="UserId"
                                    displayProp="Email"
                                    :loading="state.isLoading"
                                    :paging="true"
                                    :allLoaded="!state.isLoading"
                                    @init="getUsers()"
                                    @update:selectedItems="(newVal: any[]) => (state.newUserIds = newVal.map((user) => user.UserId))" />

                                <button class="btn btn-primary" type="button" @click="handleAddUsers" :disabled="state.newUserIds.length === 0">
                                    {{ $Resources.AclGroupAddUserBtn }}
                                </button>

                                <button class="btn btn-subtle" type="button" @click="state.showAddUser = false">
                                    {{ $Resources.AclAbortTooltip }}
                                </button>
                            </div>
                        </div>
                    </template>
                    <template #item="{ item }">
                        <td>
                            <span>{{ item.UserName }}</span>
                        </td>
                        <td>{{ item.Name }}</td>
                        <td>
                            <button
                                v-popconfirm="{
                                    body: $Resources.AclDeleteUserText,
                                    callback: () => handleRemoveUser(item),
                                    placement: 'left',
                                }"
                                class="btn btn-default"
                                type="button">
                                <i class="glyphicon glyphicon-remove"></i>
                                <span class="sr-only">{{ $Resources.AclGroupUserRemove }}</span>
                            </button>
                        </td>
                    </template>
                </List>
            </tab>
        </tabs>
    </template>
</template>

<style scoped>
    .header-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }
</style>
